'use client';

import { useEffect, useRef } from 'react';

import { Box, Container, Stack, Typography } from '@mui/material';
import Image from 'next/image';

import SocialButton from '@/components/SocialButton';
import Dribbble from '@/components/SvgIcons/Socials/Dribbble';
import Instagram from '@/components/SvgIcons/Socials/Instagram';
import LinkedIn from '@/components/SvgIcons/Socials/LinkedIn';
import World from '@/components/SvgIcons/World';
import { SocialIconName, SocialLink } from '@/config/case-studies.config';
import { Image as ImageType } from '@/types/image.type';
import { Video as VideoType } from '@/types/video.type';
import useBreakpoint from '@/utils/hooks/useBreakpoint';
import useIntersectionObserver from '@/utils/hooks/useIntersectionObserver';
import useMountFadeUpAnimation from '@/utils/hooks/useMountFadeUpAnimation';

import styles from './HeroSection.module.scss';

const ICON_MAP: Record<SocialIconName, React.ComponentType> = {
  instagram: Instagram,
  linkedin: LinkedIn,
  dribbble: Dribbble,
  website: World,
};

interface HeroSectionProps {
  title: string;
  socialLinks: SocialLink[];
  image?: ImageType;
  video?: VideoType;
  imageMobile?: ImageType;
  videoMobile?: VideoType;
}

const HeroSection = ({ title, socialLinks, image, imageMobile, video, videoMobile }: HeroSectionProps) => {
  const isMobile = useBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaWrapperRef = useRef<HTMLDivElement>(null);

  useMountFadeUpAnimation({ triggerRef: containerRef });

  const imageSrc = isMobile && imageMobile?.src ? imageMobile.src : image?.src;
  const videoSrc = isMobile && videoMobile?.src ? videoMobile.src : video?.src;

  const isInViewport = useIntersectionObserver(mediaWrapperRef, {
    threshold: 0.2,
    triggerOnce: false,
    rootMargin: '100px 0px',
  });

  useEffect(() => {
    if (!video || !videoRef.current) return undefined;

    const videoElement = videoRef.current;
    let canPlayHandler: (() => void) | null = null;

    if (isInViewport) {
      const playPromise = videoElement.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          canPlayHandler = () => {
            videoElement.play().catch(() => {});
          };

          videoElement.addEventListener('canplay', canPlayHandler, { once: true });
        });
      }
    } else {
      videoElement.pause();
    }

    return () => {
      if (canPlayHandler) {
        videoElement.removeEventListener('canplay', canPlayHandler);
      }
    };
  }, [isInViewport, video]);

  const renderMedia = () => {
    if (video) {
      return (
        <video
          ref={videoRef}
          poster={video.poster}
          loop
          muted
          playsInline
          preload="metadata"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          aria-label={video.alt}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      );
    }

    if (image && imageSrc) {
      const isGif = imageSrc.endsWith('.gif');

      return <Image src={imageSrc} alt={image.alt} fill priority style={{ objectFit: 'cover' }} unoptimized={isGif} />;
    }

    return null;
  };

  return (
    <Container ref={containerRef} maxWidth={false} disableGutters className={styles.container}>
      <Container component="section" maxWidth="xl" className={styles.content}>
        <Box ref={mediaWrapperRef} className={styles.mediaWrapper}>
          {renderMedia()}
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          mt={4}
          gap={3}
        >
          <Typography variant="h5" component="h1">
            {title}
          </Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            {socialLinks.map(({ href, icon }) => {
              const IconComponent = ICON_MAP[icon];

              return <SocialButton key={href} name={href} href={href} icon={IconComponent} />;
            })}
          </Stack>
        </Stack>
      </Container>
    </Container>
  );
};

export default HeroSection;
